import React, { useRef, useState } from "react";
import { TspFormField } from "../../../../components/primitives";
import { ADVICE_SECTIONS, ADVICE_SECTION_MAX } from "../../../../constants/tspAdvice";
import { ACCEPTED_FILE_TYPES, MEDIA_CAPTION_MAX, formatFileSize, validateMediaUpload } from "../../../media-gallery/mediaModel";
import MediaThumb from "../../../media-gallery/components/MediaThumb";
import { validateAdviceSections } from "../../addJourneyCityDetails";
import SectionShell, { FormButtons, SectionForm, autoVisitNote } from "./SectionShell";

// Media and Advice.
// Media: photos / videos of the place's visit. store.media = [{ id, caption,
// fileName?, size?, file_url?, media_type?, canDelete }]. A file cannot be
// changed once uploaded (the server has no edit for media): remove it and add
// it again. Advice: the Advice Repository's five sections for this place.
// store.advice = [{ id, sections: { best_areas, … } }].
// `showMedia` / `showAdvice`: whether the caller may see each half at all.
export default function MediaAdviceSection({ idPrefix, place, store, showMedia = true, showAdvice = true, canUpload, canAdvise, disabled }) {
  return (
    <div className="tsp-pc-split">
      {showMedia && (
        <SectionShell
          idPrefix={`${idPrefix}-media`}
          title="Media"
          addLabel="Add Photo / Video"
          emptyText="No photos or videos for this place yet."
          items={store.media}
          loading={store.mediaLoading}
          error={store.mediaError}
          onRetry={store.reloadMedia}
          canAdd={Boolean(canUpload)}
          onAdd={store.addMedia}
          onRemove={store.removeMedia}
          canRemove={(item) => Boolean(canUpload) && item.canDelete !== false}
          confirmRemove="Remove this photo / video?"
          removeLabel={(item) => `Remove ${item.caption}`}
          disabled={disabled}
          renderItem={(item) => (
            <span className="tsp-pc-media">
              {item.file_url && (
                <span className="tsp-pc-media-thumb">
                  <MediaThumb item={item} />
                </span>
              )}
              <span>
                <strong>{item.caption}</strong>
                {item.fileName && (
                  <span className="tsp-muted-text">
                    {" "}
                    · {item.fileName}
                    {item.size ? ` (${formatFileSize(item.size)})` : ""} · uploads when the journey is created
                  </span>
                )}
              </span>
            </span>
          )}
          renderForm={(form) => <MediaForm {...form} />}
        />
      )}
      {showAdvice && (
        <SectionShell
          idPrefix={`${idPrefix}-advice`}
          title="Advice"
          addLabel="Add Advice"
          emptyText="No advice written for this place yet."
          items={store.advice}
          max={store.adviceMax || Infinity}
          loading={store.adviceLoading}
          error={store.adviceError}
          onRetry={store.reloadAdvice}
          canAdd={Boolean(canAdvise)}
          canEdit={(item) => Boolean(canAdvise) && item.canEdit !== false}
          canRemove={(item) => Boolean(canAdvise) && item.canEdit !== false}
          onAdd={store.addAdvice}
          onUpdate={store.updateAdvice}
          onRemove={store.removeAdvice}
          confirmRemove="Remove this advice?"
          removeLabel={() => "Remove this advice"}
          disabled={disabled}
          renderItem={(item) => (
            <dl className="tsp-pc-facts tsp-pc-facts-stacked">
              {ADVICE_SECTIONS.filter((section) => (item.sections[section.key] || "").trim()).map((section) => (
                <div key={section.key}>
                  <dt>{section.label}</dt>
                  <dd>{item.sections[section.key]}</dd>
                </div>
              ))}
            </dl>
          )}
          renderForm={(form) => <AdviceForm {...form} />}
        />
      )}
    </div>
  );
}

function MediaForm({ submit, cancel, busy, idPrefix }) {
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState("");
  const [error, setError] = useState("");
  const input = useRef(null);

  const onSubmit = () => {
    const found = validateMediaUpload({ caption, journeyId: "x", cityVisitId: "x" }, file);
    const message = found.file || found.caption;
    if (message) {
      setError(message);
      return;
    }
    submit({ file, caption: caption.trim() });
  };

  return (
    <SectionForm onSubmit={onSubmit}>
      <div className="tsp-aj-grid">
        <TspFormField id={`${idPrefix}-file`} label="Photo / Video" required>
          <input
            ref={input}
            id={`${idPrefix}-file`}
            type="file"
            accept={ACCEPTED_FILE_TYPES}
            className="tsp-input"
            disabled={busy}
            onChange={(event) => {
              setFile(event.target.files?.[0] || null);
              setError("");
            }}
          />
        </TspFormField>
        <TspFormField id={`${idPrefix}-caption`} label="Caption" required>
          <input
            id={`${idPrefix}-caption`}
            type="text"
            className="tsp-input"
            maxLength={MEDIA_CAPTION_MAX}
            value={caption}
            disabled={busy}
            onChange={(event) => {
              setCaption(event.target.value);
              setError("");
            }}
          />
        </TspFormField>
      </div>
      {error && (
        <p className="tsp-form-error" role="alert">
          {error}
        </p>
      )}
      <FormButtons busy={busy} onSave={onSubmit} onCancel={cancel} saveLabel="Save Photo / Video" />
    </SectionForm>
  );
}

function AdviceForm({ initial, submit, cancel, busy, idPrefix }) {
  const [sections, setSections] = useState(() => Object.fromEntries(ADVICE_SECTIONS.map((section) => [section.key, initial ? initial.sections[section.key] || "" : ""])));
  const [error, setError] = useState("");

  const onSubmit = () => {
    if (!ADVICE_SECTIONS.some((section) => (sections[section.key] || "").trim())) {
      setError("Write at least one section of advice.");
      return;
    }
    const problem = validateAdviceSections(sections);
    if (problem) {
      setError(problem);
      return;
    }
    submit(sections);
  };

  return (
    <SectionForm onSubmit={onSubmit}>
      <p className="tsp-muted-text">Write any of the sections that apply.</p>
      <div className="tsp-aj-grid">
        {ADVICE_SECTIONS.map((section) => (
          <TspFormField key={section.key} id={`${idPrefix}-${section.key}`} label={section.label}>
            <textarea
              id={`${idPrefix}-${section.key}`}
              className="tsp-input"
              rows={3}
              maxLength={ADVICE_SECTION_MAX}
              placeholder={section.hint}
              value={sections[section.key]}
              disabled={busy}
              onChange={(event) => {
                setSections({ ...sections, [section.key]: event.target.value });
                setError("");
              }}
            />
          </TspFormField>
        ))}
      </div>
      {error && (
        <p className="tsp-form-error" role="alert">
          {error}
        </p>
      )}
      <FormButtons busy={busy} onSave={onSubmit} onCancel={cancel} saveLabel="Save Advice" />
    </SectionForm>
  );
}
